import type { RentalRevenue, RevenueGrain } from '@/api/reports';
import type { PrintLetterhead } from '@core/api/bookings';
import { formatAmount } from '@core/format/money';
import { rangeWords } from '@core/reports/garmentMovement';
import {
  changeOn,
  changeWords,
  grainWords,
  revenueKinds,
  rowLabel,
} from '@core/reports/rentalRevenue';

import { documentClass, type PaperSize } from './paper';

/**
 * Report C2 on paper — rental revenue: the shop, the range, a row per period with each kind of
 * charge, before GST, GST, billed and the change on last year, and the totals underneath. The
 * screen's charts stay on the screen; a printed report reads as one table. Web only, through
 * `PrintFrame`, on A4 landscape for its ten columns.
 */
export function RentalRevenueSheet({
  paper,
  report,
  grain,
  letterhead,
  branchName,
}: {
  paper: PaperSize;
  report: RentalRevenue;
  grain: RevenueGrain;
  letterhead: PrintLetterhead | undefined;
  branchName: string;
}) {
  const symbol = letterhead?.currencySymbol ?? '₹';
  const cell = (value: number) => (value === 0 ? '—' : formatAmount(value));

  return (
    <article id="printableCard" className={`${documentClass(paper)} px-7 py-6`}>
      <header className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-[1.35em] leading-tight font-bold">
            {letterhead?.tenantName ?? branchName}
          </p>
          <p className="text-[0.85em]">{letterhead?.branchName ?? branchName}</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[1.05em] font-bold tracking-[0.16em] uppercase">Rental revenue</p>
          <p className="text-[0.85em] text-[color:var(--doc-muted)]">
            {rangeWords(report.from, report.to)} · amounts in {symbol}
          </p>
        </div>
      </header>

      <table className="mt-5 w-full border-collapse text-[0.9em]">
        <thead>
          <tr className="border-b border-[color:var(--doc-rule-strong)] text-right text-[0.85em] text-[color:var(--doc-muted)]">
            <th className="py-1.5 pr-2 text-left font-semibold">{grainWords[grain]}</th>
            {revenueKinds.map((kind) => (
              <th key={kind.key} className="py-1.5 pl-2 font-semibold">
                {kind.label}
              </th>
            ))}
            <th className="py-1.5 pl-2 font-semibold">Before GST</th>
            <th className="py-1.5 pl-2 font-semibold">GST</th>
            <th className="py-1.5 pl-2 font-semibold">Billed</th>
            <th className="py-1.5 pl-2 font-semibold">On last year</th>
          </tr>
        </thead>
        <tbody>
          {report.rows.map((row) => (
            <tr
              key={row.from}
              className="border-b border-[color:var(--doc-rule)] text-right break-inside-avoid"
            >
              <td className="py-1 pr-2 text-left">{rowLabel(row, grain)}</td>
              {revenueKinds.map((kind) => (
                <td key={kind.key} className="py-1 pl-2 font-mono">
                  {cell(row.kinds[kind.key])}
                </td>
              ))}
              <td className="py-1 pl-2 font-mono font-semibold">{cell(row.earned)}</td>
              <td className="py-1 pl-2 font-mono">{cell(row.gst)}</td>
              <td className="py-1 pl-2 font-mono">{cell(row.billed)}</td>
              <td className="py-1 pl-2">{changeWords(changeOn(row.earned, row.lastYearEarned))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-[color:var(--doc-rule-strong)] text-right font-bold">
            <td className="py-1.5 pr-2 text-left">Total</td>
            {revenueKinds.map((kind) => (
              <td key={kind.key} className="py-1.5 pl-2 font-mono">
                {formatAmount(report.kinds[kind.key])}
              </td>
            ))}
            <td className="py-1.5 pl-2 font-mono">{formatAmount(report.earned)}</td>
            <td className="py-1.5 pl-2 font-mono">{formatAmount(report.gst)}</td>
            <td className="py-1.5 pl-2 font-mono">{formatAmount(report.billed)}</td>
            <td className="py-1.5 pl-2">
              {changeWords(changeOn(report.earned, report.lastYearEarned))}
            </td>
          </tr>
        </tfoot>
      </table>

      <p className="mt-4 text-[0.8em] text-[color:var(--doc-muted)]">
        From the rental invoices, by invoice date; cancelled invoices left out. Rent includes
        extensions; damage includes accessories not returned. Deposits are not revenue. "On last
        year" compares with the same dates a year earlier.
      </p>
    </article>
  );
}
